import React from 'react';

import { filterStore } from '@/controllers';
import { useKeypress } from '@/utils/hooks';

import Filters from './filters';
import { useUpdateQueryString } from './services/query-service';
import { inputStore, SearchInputStateProvider } from './stores/input';
import { queryStore } from './stores/query';
import SelectedParams from './selected-params';
import SearchInput from './search-input';
import AvailableScopes from './available-scopes';
import * as styles from './styles';
import { SearchableType } from './types';

type SearchInputHandle = React.ElementRef<typeof SearchInput>;

const SearchV2: React.FC = () => {
  const { state: queryState, actions: queryActions } = React.useContext(queryStore);
  const { state: filterState, actions: filterActions } = React.useContext(filterStore);

  useUpdateQueryString(queryState.params, queryActions.addParam, filterActions, filterState);

  const inputStoreContext = React.useContext(inputStore);
  const { setInputValue, clearInputValue, setAutoCompleting } = inputStoreContext.actions;

  const [isInputFocused, setIsInputFocused] = React.useState(false);

  /*
   * NOTE:
   * This ref allows us to only attach keypress events to the designated input area.
   */
  const inputAreaRef = React.useRef<HTMLDivElement>(document.createElement('div'));

  /*
   * NOTE:
   * This ref allows us to control the underlying <input /> element.
   */
  const searchInputRef = React.useRef<SearchInputHandle>(document.createElement('input'));

  React.useEffect(
    () => (isInputFocused ? searchInputRef.current.focus() : searchInputRef.current.blur()),
    [isInputFocused]
  );

  useKeypress('/', () => {
    setIsInputFocused(true);
  });

  useKeypress(
    'Escape',
    () => {
      clearInputValue();
      setAutoCompleting(false);
      setIsInputFocused(false);
    },
    inputAreaRef.current
  );

  useKeypress(
    'Backspace',
    () => {
      const keys = Object.keys(queryState.params);
      if (!keys.length) return;
      const scope = keys[keys.length - 1];

      queryActions.removeParam({ scope });
    },
    inputAreaRef.current
  );

  const addFromAvailableScopesToInputArea = (scope: SearchableType) => {
    setInputValue(scope);
    setAutoCompleting(true);
  };

  return (
    <div css={styles.root}>
      <AvailableScopes
        scopes={queryState.scopes}
        handleOnClick={addFromAvailableScopesToInputArea}
      />
      <div css={[styles.main, styles.setIsInputFocusedStyle(isInputFocused)]}>
        <div css={styles.inputArea} ref={inputAreaRef}>
          <SelectedParams params={queryState.params} removeParam={queryActions.removeParam} />

          <SearchInput
            scopes={queryState.scopes}
            forwardedRef={searchInputRef}
            isInputFocused={isInputFocused}
            setIsInputFocused={setIsInputFocused}
          />
        </div>
        <div css={styles.filtersArea}>
          <Filters />
        </div>
      </div>
    </div>
  );
};

const SearchV2WithStateProvider: React.FC = () => (
  <SearchInputStateProvider>
    <SearchV2 />
  </SearchInputStateProvider>
);

export default SearchV2WithStateProvider;
